// Re-export the Supabase client from the consolidated client file
import { supabase } from '../config/supabaseClient'
export { supabase }

console.log('Using consolidated Supabase client')

// Types for our database tables
export interface BusinessIdea {
  id?: string;
  user_id: string;
  title: string;
  description?: string;
  target_market?: string;
  industry?: string;
  revenue_model?: string | object;
  products?: any;
  services?: any;
  details?: any;
  created_at?: string;
  updated_at?: string;
}

export interface BusinessPlan {
  id?: string;
  user_id: string;
  idea_id?: string;
  content?: any;
  sections?: any;
  status?: string;
  created_at?: string;
  updated_at?: string;
  version?: number;
  title?: string;
  additional_data?: any;
}

// Helper functions for database operations
export const supabaseHelper = {
  // Ideas
  async createIdea(idea: Partial<BusinessIdea>) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        console.error('No authenticated user found');
        throw new Error('Not authenticated');
      }
      
      console.log('Creating idea for authenticated user:', user.email);
      
      // Ensure user exists in users table
      await this.ensureUserExists(user);

      const { data, error } = await supabase
        .from('ideas')
        .insert({
          ...idea,
          user_id: user.id
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating idea:', error);
      throw error;
    }
  },

  async getIdeas() {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        console.error('No authenticated user found');
        throw new Error('Not authenticated');
      }
      
      console.log('Getting ideas for authenticated user:', user.email);
      
      // Ensure user exists in users table
      await this.ensureUserExists(user);

      const { data, error } = await supabase
        .from('ideas')
        .select('*')
        .eq('user_id', user.id);

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error getting ideas:', error);
      return [];
    }
  },

  // Business Plans
  async createBusinessPlan(plan: Partial<BusinessPlan>) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        console.error('No authenticated user found');
        throw new Error('Not authenticated');
      }
      
      console.log('Creating business plan for authenticated user:', user.email);
      
      // Ensure user exists in users table
      await this.ensureUserExists(user);

      const { data, error } = await supabase
        .from('business_plans')
        .upsert({
          ...plan,
          user_id: user.id,
          updated_at: new Date().toISOString(),
          created_at: plan.created_at || new Date().toISOString()
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating business plan:', error);
      throw error;
    }
  },

  async getBusinessPlan(ideaId: string) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        console.error('No authenticated user found');
        throw new Error('Not authenticated');
      }
      
      console.log('Getting completed business plan for authenticated user:', user.email);
      
      // Ensure user exists in users table
      await this.ensureUserExists(user);

      // First check if any completed plans exist with this idea_id to avoid 406 errors
      console.log(`Checking if any completed business plans exist for idea ID: ${ideaId}`);
      const { count, error: countError } = await supabase
        .from('business_plans')
        .select('*', { count: 'exact', head: true })
        .eq('idea_id', ideaId)
        .eq('user_id', user.id)
        .eq('status', 'completed'); // Only count completed plans

      if (countError) {
        console.error('Error checking if business plan exists:', countError);
        return null; // Return null instead of throwing to avoid cascading errors
      }

      // If no completed plans exist, return null early
      if (count === 0 || !count) {
        console.log(`No completed business plans found for idea ID: ${ideaId}`);
        return null;
      }

      console.log(`Found ${count} completed business plan(s) for idea ID: ${ideaId}, fetching details...`);
      
      // Now fetch the actual plan
      const { data, error } = await supabase
        .from('business_plans')
        .select('*')
        .eq('idea_id', ideaId)
        .eq('user_id', user.id)
        .eq('status', 'completed') // Only get completed plans
        .maybeSingle(); // Use maybeSingle instead of single to avoid 406 errors

      if (error) {
        console.error('Error fetching business plan details:', error);
        return null;
      }

      if (!data) {
        console.log(`No completed business plan data returned for idea ID: ${ideaId}`);
        return null;
      }

      console.log(`Successfully retrieved completed business plan for idea ID: ${ideaId}`);
      return data;
    } catch (error) {
      console.error('Error getting business plan:', error);
      return null;
    }
  },

  // Helper function to ensure user exists in users table
  async ensureUserExists(user: { id: string, email?: string }) {
    try {
      // Check if the user exists in the users table
      const { data: userRecord, error: userCheckError } = await supabase
        .from('users')
        .select('id')
        .eq('id', user.id)
        .single();

      // If the user doesn't exist, create it
      if (userCheckError || !userRecord) {
        console.log('User not found in users table, creating user record:', user.id);
        const { error: createUserError } = await supabase
          .from('users')
          .insert({
            id: user.id,
            email: user.email,
            created_at: new Date().toISOString()
          });

        if (createUserError) {
          console.error('Failed to create user record:', createUserError);
          throw createUserError;
        }
      }
      
      return true;
    } catch (error) {
      console.error('Error in ensureUserExists:', error);
      throw error;
    }
  },

  // Storage
  async uploadDocument(file: File, path: string) {
    return await supabase.storage
      .from('documents')
      .upload(path, file)
  },

  async getDocument(path: string) {
    return await supabase.storage
      .from('documents')
      .download(path)
  }
}
